/**
 * Line icons, drawn to match the chrome in the reference screenshots:
 * 24px grid, 1.8 stroke, round caps, no fills.
 */
interface Props {
  size?: number;
  className?: string;
}

const base = (size: number) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
});

export const IconCommand = ({ size = 22 }: Props) => (
  <svg {...base(size)}>
    <path d="M9 6a3 3 0 1 0-3 3h12a3 3 0 1 0-3-3v12a3 3 0 1 0 3-3H6a3 3 0 1 0 3 3V6Z" />
  </svg>
);

/** A toothed cog. The earlier radiating-lines version read as a brightness sun. */
export const IconGear = ({ size = 22 }: Props) => (
  <svg {...base(size)}>
    <circle cx="12" cy="12" r="3.1" />
    <path d="M19.1 14.6a1.5 1.5 0 0 0 .3 1.66l.06.05a1.85 1.85 0 1 1-2.62 2.62l-.05-.06a1.5 1.5 0 0 0-1.66-.3 1.5 1.5 0 0 0-.91 1.38v.15a1.85 1.85 0 0 1-3.7 0v-.08a1.5 1.5 0 0 0-.98-1.37 1.5 1.5 0 0 0-1.66.3l-.05.06a1.85 1.85 0 1 1-2.62-2.62l.06-.05a1.5 1.5 0 0 0 .3-1.66 1.5 1.5 0 0 0-1.38-.91h-.15a1.85 1.85 0 0 1 0-3.7h.08a1.5 1.5 0 0 0 1.37-.98 1.5 1.5 0 0 0-.3-1.66l-.06-.05a1.85 1.85 0 1 1 2.62-2.62l.05.06a1.5 1.5 0 0 0 1.66.3h.07a1.5 1.5 0 0 0 .91-1.38v-.15a1.85 1.85 0 0 1 3.7 0v.08a1.5 1.5 0 0 0 .91 1.37 1.5 1.5 0 0 0 1.66-.3l.05-.06a1.85 1.85 0 1 1 2.62 2.62l-.06.05a1.5 1.5 0 0 0-.3 1.66v.07a1.5 1.5 0 0 0 1.38.91h.15a1.85 1.85 0 0 1 0 3.7h-.08a1.5 1.5 0 0 0-1.37.91Z" />
  </svg>
);

export const IconImage = ({ size = 22 }: Props) => (
  <svg {...base(size)}>
    <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
    <circle cx="8.4" cy="9.6" r="1.5" />
    <path d="m3.6 17.2 4.6-4.4a2 2 0 0 1 2.7 0l3 2.8m0 0 1.7-1.6a2 2 0 0 1 2.7 0l2 1.9m-6.4-.3 3.1 3" />
  </svg>
);

export const IconCamera = ({ size = 22 }: Props) => (
  <svg {...base(size)}>
    <path d="M3 8.6A2 2 0 0 1 5 6.6h1.9l1.2-2h7.8l1.2 2H19a2 2 0 0 1 2 2v8.8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
    <circle cx="12" cy="13" r="3.6" />
  </svg>
);

export const IconChat = ({ size = 22 }: Props) => (
  <svg {...base(size)}>
    <path d="M4 5.6h16a1 1 0 0 1 1 1v9.2a1 1 0 0 1-1 1H9.6L5 20.4v-3.6H4a1 1 0 0 1-1-1V6.6a1 1 0 0 1 1-1Z" />
  </svg>
);

export const IconFolder = ({ size = 22 }: Props) => (
  <svg {...base(size)}>
    <path d="M3 7.2a1.8 1.8 0 0 1 1.8-1.8h3.9l2 2.2h7.5A1.8 1.8 0 0 1 20 9.4v7.4a1.8 1.8 0 0 1-1.8 1.8H4.8A1.8 1.8 0 0 1 3 16.8Z" />
  </svg>
);

export const IconPrint = ({ size = 22 }: Props) => (
  <svg {...base(size)}>
    <path d="M7 9V4h10v5" />
    <rect x="3.5" y="9" width="17" height="7.4" rx="1.8" />
    <rect x="7" y="14" width="10" height="6" rx="1" />
  </svg>
);

export const IconShare = ({ size = 22 }: Props) => (
  <svg {...base(size)}>
    <path d="M12 15.4V3.6M8.4 7.2 12 3.6l3.6 3.6" />
    <path d="M5.5 12.6v6.2a1.6 1.6 0 0 0 1.6 1.6h9.8a1.6 1.6 0 0 0 1.6-1.6v-6.2" />
  </svg>
);

/** The angled highlighter/marker glyph from the screenshots. */
export const IconMarker = ({ size = 24 }: Props) => (
  <svg {...base(size)}>
    <path d="M14.6 3.8 20.2 9.4 10.9 18.7a2 2 0 0 1-1 .54l-4.2.9.9-4.2a2 2 0 0 1 .54-1Z" />
    <path d="m12.8 5.6 5.6 5.6" />
    <path d="M4 21.2h7" strokeWidth="2.4" />
  </svg>
);

export const IconPen = ({ size = 24 }: Props) => (
  <svg {...base(size)}>
    <path d="M4 20.4 4.9 16a2 2 0 0 1 .54-1L15.7 4.7a2.1 2.1 0 0 1 3 3L8.4 18a2 2 0 0 1-1 .54Z" />
    <path d="m14.2 6.2 3.6 3.6" />
  </svg>
);

export const IconHighlighter = ({ size = 24 }: Props) => (
  <svg {...base(size)}>
    <path d="M9.4 15.6 4.9 20h4.3l2.1-2.1" />
    <path d="M13.4 3.9 20 10.5l-6.6 6.6-6.6-6.6Z" />
  </svg>
);

export const IconEraser = ({ size = 24 }: Props) => (
  <svg {...base(size)}>
    <path d="m8.6 20.4-4-4a2 2 0 0 1 0-2.9l8-8a2 2 0 0 1 2.9 0l4.1 4.1a2 2 0 0 1 0 2.9l-7.9 7.9Z" />
    <path d="M7.4 12.2 15.8 20.6M9 20.4h11" />
  </svg>
);

/** Tipped paint bucket with a drip, for the fill tool. */
export const IconFill = ({ size = 24 }: Props) => (
  <svg {...base(size)}>
    <path d="M11.2 3.4 4.6 10a1.4 1.4 0 0 0 0 2l6 6a1.4 1.4 0 0 0 2 0l6-6-7.4-7.4Z" />
    <path d="m8.4 6.2-2-2" />
    <path d="M20.6 14.2c1 1.3 1.6 2.3 1.6 3.1a1.7 1.7 0 0 1-3.4 0c0-.8.6-1.8 1.8-3.1Z" />
  </svg>
);

export const IconScissors = ({ size = 24 }: Props) => (
  <svg {...base(size)}>
    <circle cx="6.5" cy="17.5" r="2.6" />
    <circle cx="6.5" cy="6.5" r="2.6" />
    <path d="M8.6 8.4 20 19.4M20 4.6 8.6 15.6" />
  </svg>
);

/** Arrow cursor with a selection frame, for the select tool. */
export const IconSelect = ({ size = 24 }: Props) => (
  <svg {...base(size)}>
    <path d="M4.6 3.4 6.4 15l2.7-3.2 3.1 5.4 2.4-1.4-3.1-5.3 4.2-.3Z" />
    <path d="M14.6 20.6h5.8v-5.8" strokeDasharray="2.6 2.2" />
  </svg>
);

/** Overlapping shapes, for the shapes tool. */
export const IconShapes = ({ size = 24 }: Props) => (
  <svg {...base(size)}>
    <rect x="3.2" y="10.8" width="10" height="10" rx="1.6" />
    <circle cx="15.6" cy="8.4" r="5.2" />
  </svg>
);

export const IconLine = ({ size = 24 }: Props) => (
  <svg {...base(size)}><path d="M4.6 19.4 19.4 4.6" /></svg>
);

export const IconArrow = ({ size = 24 }: Props) => (
  <svg {...base(size)}>
    <path d="M4.6 19.4 19.4 4.6M12.4 4.6h7v7" />
  </svg>
);

export const IconRect = ({ size = 24 }: Props) => (
  <svg {...base(size)}><rect x="4" y="6" width="16" height="12" rx="2" /></svg>
);

export const IconCircle = ({ size = 24 }: Props) => (
  <svg {...base(size)}><circle cx="12" cy="12" r="7.6" /></svg>
);

export const IconText = ({ size = 24 }: Props) => (
  <svg {...base(size)}><path d="M5 6.4V4.6h14v1.8M12 4.6v14.8M8.8 19.4h6.4" /></svg>
);

export const IconStamp = ({ size = 24 }: Props) => (
  <svg {...base(size)}>
    <path d="m12 3.4 2.5 5.3 5.8.8-4.2 4 1 5.7L12 16.5 6.9 19.2l1-5.7-4.2-4 5.8-.8Z" />
  </svg>
);

/** A glowing stroke, for the neon brush. */
export const IconNeon = ({ size = 24 }: Props) => (
  <svg {...base(size)}>
    <path d="M4.4 16.6c3.6-6.4 7.2-9.6 10.8-9.6 2.4 0 3.6 1.2 3.6 3.2 0 2.6-2.2 4-4.4 4-1.6 0-2.4-.8-2.4-1.9 0-1.5 1.5-2.5 3-2.5" />
    <path d="M2.6 20.4h18.8" opacity=".45" strokeWidth="2.4" />
  </svg>
);

export const IconLaser = ({ size = 24 }: Props) => (
  <svg {...base(size)}>
    <circle cx="12" cy="12" r="2.4" />
    <path d="M12 3.2v2.6M12 18.2v2.6M20.8 12h-2.6M5.8 12H3.2" />
  </svg>
);

export const IconHand = ({ size = 24 }: Props) => (
  <svg {...base(size)}>
    <path d="M8 11V5.4a1.6 1.6 0 0 1 3.2 0V11m0-1.4V4.4a1.6 1.6 0 0 1 3.2 0V11m0-1.2a1.6 1.6 0 0 1 3.2 0V16a5 5 0 0 1-5 5h-1.4a5 5 0 0 1-4-2l-2.9-4a1.6 1.6 0 0 1 2.5-2L8 14.4V8.2a1.6 1.6 0 0 1 0-.2" />
  </svg>
);

/** The crosshair "find me" glyph from the bottom-right cluster. */
export const IconTarget = ({ size = 22 }: Props) => (
  <svg {...base(size)}>
    <circle cx="12" cy="12" r="3.4" />
    <circle cx="12" cy="12" r="7.6" />
    <path d="M12 1.8v2.8M12 19.4v2.8M22.2 12h-2.8M4.6 12H1.8" />
  </svg>
);

export const IconUndo = ({ size = 22 }: Props) => (
  <svg {...base(size)}>
    <path d="M8.4 6.2 4 10.6l4.4 4.4" />
    <path d="M4 10.6h9.6a5.8 5.8 0 0 1 0 11.6h-1.4" />
  </svg>
);

export const IconRedo = ({ size = 22 }: Props) => (
  <svg {...base(size)}>
    <path d="m15.6 6.2 4.4 4.4-4.4 4.4" />
    <path d="M20 10.6h-9.6a5.8 5.8 0 0 0 0 11.6h1.4" />
  </svg>
);

export const IconTrash = ({ size = 22 }: Props) => (
  <svg {...base(size)}>
    <path d="M4 6.6h16M9.4 6.6V4.4a1 1 0 0 1 1-1h3.2a1 1 0 0 1 1 1v2.2" />
    <path d="M6.4 6.6 7.3 20a1.4 1.4 0 0 0 1.4 1.3h6.6a1.4 1.4 0 0 0 1.4-1.3l.9-13.4" />
    <path d="M10.4 10.4v6.8M13.6 10.4v6.8" />
  </svg>
);

export const IconUsers = ({ size = 22 }: Props) => (
  <svg {...base(size)}>
    <circle cx="9" cy="8.4" r="3.4" />
    <path d="M2.8 20a6.2 6.2 0 0 1 12.4 0" />
    <path d="M16.2 5.4a3.4 3.4 0 0 1 0 6.6M17.6 14.4A6.2 6.2 0 0 1 21.2 20" />
  </svg>
);

export const IconPlus = ({ size = 22 }: Props) => (
  <svg {...base(size)}><path d="M12 5v14M5 12h14" /></svg>
);

export const IconMinus = ({ size = 22 }: Props) => (
  <svg {...base(size)}><path d="M5 12h14" /></svg>
);

export const IconClose = ({ size = 22 }: Props) => (
  <svg {...base(size)}><path d="M6 6l12 12M18 6 6 18" /></svg>
);

export const IconCheck = ({ size = 22 }: Props) => (
  <svg {...base(size)}><path d="m4.8 12.6 4.6 4.6 9.8-9.8" /></svg>
);

export const IconCopy = ({ size = 22 }: Props) => (
  <svg {...base(size)}>
    <rect x="8.6" y="8.6" width="11.8" height="11.8" rx="2.2" />
    <path d="M15.4 8.6V5.8a2.2 2.2 0 0 0-2.2-2.2H5.8a2.2 2.2 0 0 0-2.2 2.2v7.4a2.2 2.2 0 0 0 2.2 2.2h2.8" />
  </svg>
);

export const IconLock = ({ size = 22 }: Props) => (
  <svg {...base(size)}>
    <rect x="4.6" y="10.4" width="14.8" height="10" rx="2.4" />
    <path d="M8 10.4V7.6a4 4 0 0 1 8 0v2.8" />
  </svg>
);

export const IconEye = ({ size = 22 }: Props) => (
  <svg {...base(size)}>
    <path d="M2.4 12S5.9 5.6 12 5.6 21.6 12 21.6 12 18.1 18.4 12 18.4 2.4 12 2.4 12Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export const IconBack = ({ size = 22 }: Props) => (
  <svg {...base(size)}><path d="M15 5 8 12l7 7" /></svg>
);

export const IconLogout = ({ size = 22 }: Props) => (
  <svg {...base(size)}>
    <path d="M14.6 16.4 19 12l-4.4-4.4M19 12H8.6" />
    <path d="M12 3.6H6.4A1.8 1.8 0 0 0 4.6 5.4v13.2a1.8 1.8 0 0 0 1.8 1.8H12" />
  </svg>
);

export const IconDownload = ({ size = 22 }: Props) => (
  <svg {...base(size)}>
    <path d="M12 3.6v11.8M8.2 11.6 12 15.4l3.8-3.8" />
    <path d="M4.6 17.4v1.8a1.8 1.8 0 0 0 1.8 1.8h11.2a1.8 1.8 0 0 0 1.8-1.8v-1.8" />
  </svg>
);

export const IconGrid = ({ size = 22 }: Props) => (
  <svg {...base(size)}>
    <rect x="3.6" y="3.6" width="16.8" height="16.8" rx="2.4" />
    <path d="M3.6 9.2h16.8M3.6 14.8h16.8M9.2 3.6v16.8M14.8 3.6v16.8" />
  </svg>
);

/** Save the drawing to the device — an image with a download arrow. */
export const IconSaveImage = ({ size = 22 }: Props) => (
  <svg {...base(size)}>
    <path d="M20.4 12.4V6.5a2 2 0 0 0-2-2H5.6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h6.2" />
    <circle cx="8.9" cy="9.4" r="1.4" />
    <path d="m4 15.6 3.6-3.4a1.9 1.9 0 0 1 2.6 0l2.4 2.2" />
    <path d="M17.6 15v6M15 18.4l2.6 2.6 2.6-2.6" />
  </svg>
);

/** Leave this sketchpad and go back to the list — a door with an arrow out. */
export const IconExit = ({ size = 22 }: Props) => (
  <svg {...base(size)}>
    <path d="M10.4 20.4H5.6a1.8 1.8 0 0 1-1.8-1.8V5.4a1.8 1.8 0 0 1 1.8-1.8h4.8" />
    <path d="M15.4 16.4 19.8 12l-4.4-4.4M19.8 12H9" />
  </svg>
);

export const IconMore = ({ size = 22 }: Props) => (
  <svg {...base(size)}>
    <circle cx="5.2" cy="12" r="1.5" fill="currentColor" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    <circle cx="18.8" cy="12" r="1.5" fill="currentColor" />
  </svg>
);

export const IconPlay = ({ size = 22 }: Props) => (
  <svg {...base(size)}><path d="M7 4.8 19 12 7 19.2Z" /></svg>
);

export const IconPause = ({ size = 22 }: Props) => (
  <svg {...base(size)}><path d="M8.6 4.8v14.4M15.4 4.8v14.4" /></svg>
);
